import { useEffect, useRef } from 'react';

import { useSearchParams } from 'react-router-dom';

import { useLocalStorage } from '@/hooks/use-local-storage';

type FilterRecord = Record<string, string | string[]>;

interface UsePersistedTableFiltersParams {
  /** Unique localStorage key for this table's filters */
  storageKey: string;
  /** All filter param names this table owns (used for read/write scoping) */
  filterKeys: string[];
  /** Applied on first visit when localStorage has no saved state */
  defaultFilters?: FilterRecord;
}

interface UsePersistedTableFiltersResult {
  /**
   * True once the URL reflects the correct initial filters.
   * When defaultFilters or savedFilters need to be written into the URL,
   * this is false on the first render and true on the next — preventing
   * the data query from firing before the URL (and thus the filters) is settled.
   */
  isReady: boolean;
}

/**
 * Persists URL-based table filters to localStorage and restores them on mount.
 *
 * Behaviour:
 * - On mount: if none of the owned filter keys are present in the URL, applies
 *   saved filters from localStorage (falling back to `defaultFilters`).
 * - On every URL change: saves the current values of the owned filter keys to
 *   localStorage so the next visit can restore them.
 *
 * Returns `isReady` — pass it as `enabled` to the data query so the query
 * does not fire before the correct filters are in the URL, avoiding a
 * double-fetch (one with empty filters, one with the restored filters).
 */
export function usePersistedTableFilters({
  storageKey,
  filterKeys,
  defaultFilters = {},
}: UsePersistedTableFiltersParams): UsePersistedTableFiltersResult {
  const [searchParams, setSearchParams] = useSearchParams();
  // null means "never been saved" → apply defaultFilters on first visit.
  // {} means "user explicitly cleared all filters" → apply nothing.
  const [savedFilters, setSavedFilters] = useLocalStorage<FilterRecord | null>(storageKey, null);

  // Determine synchronously on the first render whether we need to write
  // filters into the URL. If yes, isReady starts as false so the data query
  // is held until the URL update lands on the next render.
  const hasUrlFilters = filterKeys.some(key => searchParams.has(key));
  const filtersToApply = (() => {
    if (hasUrlFilters) return null;
    // null = first visit ever → use defaultFilters
    // {} or populated = user's last saved state → use as-is (even if empty)
    const candidate = savedFilters === null ? defaultFilters : savedFilters;
    return Object.keys(candidate).length > 0 ? candidate : null;
  })();

  // isReady is false only on the very first render when we need to write
  // filters into the URL. It flips to true permanently after that — subsequent
  // re-renders (e.g. user clearing filters) must never block the query again.
  const isReadyRef = useRef(filtersToApply === null);

  // On mount: restore saved (or default) filters when the URL has none
  useEffect(() => {
    if (!filtersToApply) {
      isReadyRef.current = true;
      return;
    }

    const newParams = new URLSearchParams(searchParams.toString());
    Object.entries(filtersToApply).forEach(([key, value]) => {
      newParams.delete(key);
      if (Array.isArray(value)) {
        value.forEach(v => newParams.append(key, v));
      } else if (value) {
        newParams.set(key, value);
      }
    });
    setSearchParams(newParams, { replace: true });
    isReadyRef.current = true;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // On every URL change: persist the owned filter keys to localStorage
  useEffect(() => {
    const current: FilterRecord = {};
    filterKeys.forEach(key => {
      const values = searchParams.getAll(key);
      if (values.length > 1) {
        current[key] = values;
      } else if (values.length === 1) {
        current[key] = values[0];
      }
    });
    setSavedFilters(current);
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  return { isReady: isReadyRef.current };
}
